// Decisão de "somente áudio" (overlay de rádio) do player LIVE.
//
// Regra de ouro: a verdade sobre "tem vídeo" vem do DEMUXER (mpegts.js
// MEDIA_INFO → `hasVideo`, parseado do PMT), NUNCA de `video.videoWidth`/
// `videoHeight`. No MSE essas dimensões ficam 0 num intervalo racy (o áudio
// costuma ser demuxado/decodado antes do 1º keyframe), então cravar audio-only
// por `videoWidth===0` marcava canal COM vídeo como "só áudio" — era o
// falso-positivo. Ver docs/reports do fix tv-audio-only-false-positive.
//
// Estas funções são PURAS e NÃO recebem `videoWidth`/`videoHeight`: é
// estruturalmente impossível reintroduzir o latch de dimensão aqui. O default é
// sempre "tem vídeo" — só marcamos audio-only quando o demuxer CONFIRMA a
// ausência de um elementary stream de vídeo (`hasVideo === false`).

import type { LivePlayerStatus } from './types'

/**
 * Status quando o `<video>` emite `playing`.
 *
 * @param mediaHasVideo verdade do demuxer sobre o canal atual:
 *   - `true`  → canal de vídeo (toca)
 *   - `null`  → MEDIA_INFO ainda não chegou → default "tem vídeo" (toca; se
 *     for rádio, o handler de `mediainfo` corrige pra audio-only depois)
 *   - `false` → rádio confirmado (sem ES de vídeo) → overlay de audio-only
 */
export function statusOnPlaying(
  mediaHasVideo: boolean | null,
): Extract<LivePlayerStatus, 'playing' | 'audio-only'> {
  return mediaHasVideo === false ? 'audio-only' : 'playing'
}

/**
 * Aplica o `hasVideo` do MEDIA_INFO ao status corrente.
 *
 * Só marca `audio-only` quando o demuxer confirma `hasVideo === false` (rádio)
 * E o canal está carregado/tocando. `hasVideo === true` NUNCA esconde vídeo
 * (inclui o caso HEVC não-decodável: o stream TEM vídeo, então não é rádio — se
 * o MSE não decodar, o caminho é erro de playback/failover, não overlay de
 * rádio). Não toca em `idle` (sem canal) nem em `error` (falho): um MEDIA_INFO
 * tardio não pode ressuscitar um canal que já falhou.
 */
export function statusOnMediaInfo(
  hasVideo: boolean,
  current: LivePlayerStatus,
): LivePlayerStatus {
  // `idle` (sem canal) e `error` (já falhou) são terminais pro sinal do
  // demuxer: um MEDIA_INFO tardio não carrega canal nem ressuscita falha.
  if (current === 'idle' || current === 'error') return current
  // Demuxer confirmou rádio (sem ES de vídeo) → overlay de audio-only.
  if (hasVideo === false) return 'audio-only'
  // Demuxer confirmou vídeo → NUNCA deixa o overlay de rádio no ar: corrige um
  // `audio-only` anterior (errar pra "tem vídeo"). `loading`/`playing` seguem
  // como estão (o handler de `playing` resolve o status final).
  if (current === 'audio-only') return 'playing'
  return current
}
